import { useId } from 'react';
import { Folder, FolderCheck, FolderMinus } from 'lucide-react';
import { IndeterminateCheckbox } from '@/components/data-table/data-table-checkbox';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

// The folder shape is drawn over the checkbox rather than replacing it: the control stays a real
// checkbox (role, aria-checked, keyboard, label association, the indeterminate state) and only its
// visual changes, so the folder bars say "this selects a folder" without giving up the semantics.
const folderGlyph = (checked: boolean, indeterminate: boolean) => {
  if (checked) {
    return FolderCheck;
  }
  return indeterminate ? FolderMinus : Folder;
};

/**
 * Tri-state select-all control with a visible text label, for the right-hand group of a toolbar.
 *
 * The label says what the next click does, so it reads "Select All" until everything is selected
 * and "Deselect All" after. `alternateLabel` is the text for the state this control is not in; it
 * is rendered invisibly underneath so the control's width is the wider of the two and the toolbar
 * never shifts as the label swaps under the pointer.
 *
 * `accessibleLabel` overrides the accessible name when the control needs more context than the
 * visible text (the folder name, the asset count). It must contain the visible `label` verbatim so
 * that speech input matching the visible text still activates the control (WCAG 2.5.3) - which
 * means it has to switch with the label, not just describe the "select" direction.
 */
export const SelectAllCheckbox = ({
  label,
  alternateLabel,
  accessibleLabel,
  checked,
  indeterminate,
  onToggle,
  disabled,
  dataCy,
  className,
  shape = 'square',
}: {
  label: string;
  alternateLabel: string;
  accessibleLabel?: string;
  checked: boolean;
  indeterminate: boolean;
  onToggle: () => void;
  disabled?: boolean;
  dataCy: string;
  className?: string;
  shape?: 'square' | 'folder';
}) => {
  const checkboxId = useId();
  const Glyph = folderGlyph(checked, indeterminate);

  const checkbox = (
    <IndeterminateCheckbox
      id={checkboxId}
      aria-label={accessibleLabel}
      checked={checked}
      indeterminate={indeterminate}
      disabled={disabled}
      onChange={onToggle}
      data-cy={dataCy}
      // Stretched over the glyph so the whole folder is the hit target, not a 16px box inside it.
      checkboxContainerClassName={cn(shape === 'folder' && 'absolute inset-0')}
      className={cn(
        shape === 'folder' &&
          // The folder glyph replaces the square frame and its tick, so the ring and the built-in
          // indicator both go. `focus-within` fires on mouse click too, which is what put a square
          // around a folder-shaped control - only `focus-visible` (keyboard) should show a ring,
          // and it sits offset so it reads as focus rather than as part of the shape.
          'icon-5! rounded-none! ring-0! focus-within:ring-0! focus-visible:rounded-sm! focus-visible:ring-2! focus-visible:ring-offset-2!',
      )}
    >
      {shape === 'folder' ? <></> : undefined}
    </IndeterminateCheckbox>
  );

  return (
    <div className={cn('flex shrink-0 items-center gap-x-2 whitespace-nowrap', className)}>
      {shape === 'folder' ? (
        // `icon-5` (20px), not the checkbox's 16px: the square control's 2px ring sits outside its
        // box, so it reads 20px wide. Matching the box would leave the folder visibly smaller.
        <span className="relative inline-flex icon-5 items-center justify-center">
          <Glyph aria-hidden className="pointer-events-none absolute inset-0 icon-5 stroke-icon-base" />
          {checkbox}
        </span>
      ) : (
        checkbox
      )}
      <Label
        htmlFor={checkboxId}
        // `text-xs font-bold text-text-gray` is the toolbar label treatment - the same one the
        // Sort / View triggers and the Remove Folder button use. This control sits in that row,
        // so it has to read as part of it rather than as a form field dropped into a toolbar.
        className={cn(
          'grid cursor-pointer text-xs font-bold text-text-gray',
          disabled && 'cursor-not-allowed opacity-50',
        )}
      >
        {/* Both labels occupy the same grid cell, so the wider one sets the width for both states. */}
        <span aria-hidden className="invisible col-start-1 row-start-1">
          {alternateLabel}
        </span>
        <span className="col-start-1 row-start-1">{label}</span>
      </Label>
    </div>
  );
};
